import { recordingSessionCreateSchema } from "@rosc/contracts";
import { z } from "zod";
import {
  assertNoObsoleteSubmission,
  discardObsoleteSubmissions,
} from "./obsolete-submissions";

const schema = recordingSessionCreateSchema.extend({
  key: z.uuid().toLowerCase(),
  kind: z.enum(["pending", "accepted"]),
});
const storageKey = (account: string) =>
  `rosc:recording-session:${encodeURIComponent(account)}`;
export const readRecordingSessionIntent = (account: string) => {
  assertNoObsoleteSubmission(account, "rosc:recording-session:");
  try {
    const value = sessionStorage.getItem(storageKey(account));
    return value === null ? null : schema.parse(JSON.parse(value));
  } catch {
    throw Error(
      "Saved microphone request cannot be read. Check the mobile app before abandoning it.",
    );
  }
};
export const prepareRecordingSessionIntent = (
  account: string,
  deviceId: string,
) => {
  const previous = readRecordingSessionIntent(account);
  if (previous?.kind === "pending") {
    if (previous.deviceId !== deviceId)
      throw Error(
        "A microphone request is unresolved. Retry it, or check the mobile app before abandoning it.",
      );
    return previous;
  }
  const next = schema.parse({
    deviceId,
    key: crypto.randomUUID(),
    kind: "pending",
  });
  try {
    sessionStorage.setItem(storageKey(account), JSON.stringify(next));
  } catch {
    throw Error(
      "Microphone retry identity could not be saved. No new request was sent.",
    );
  }
  return next;
};
export const acceptRecordingSessionIntent = (account: string, key: string) => {
  const current = readRecordingSessionIntent(account);
  if (!current || current.key !== key)
    throw Error(
      "This microphone request is no longer current. Check the mobile app.",
    );
  sessionStorage.setItem(
    storageKey(account),
    JSON.stringify({ ...current, kind: "accepted" }),
  );
};
export const abandonRecordingSessionIntent = (account: string) => {
  discardObsoleteSubmissions(account, "rosc:recording-session:");
  sessionStorage.removeItem(storageKey(account));
};
